import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { eventsAPI } from '../../api';
import toast from 'react-hot-toast';

export default function CreateEventPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', description: '', date: '', time: '', venue: '', location: '' });
  const [ticketTypes, setTicketTypes] = useState([{ name: 'General Admission', description: '', price: '', quantity: '' }]);
  const [banner, setBanner] = useState(null);

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Please use an image under 5MB.');
      e.target.value = '';
      return;
    }
    setBanner(file);
  };
  const [loading, setLoading] = useState(false);
  const [paymentPending, setPaymentPending] = useState(false);
  const [createdEventId, setCreatedEventId] = useState(null);
  const [phone, setPhone] = useState('');
  const [fee, setFee] = useState(0);

  useEffect(() => {
    fetch('/api/tikoo/super/public-settings')
      .then(r => r.json()).then(d => setFee(d.event_creation_fee || 0)).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setTT = (i, k, v) => setTicketTypes(tt => tt.map((t, idx) => idx === i ? { ...t, [k]: v } : t));
  const addTT = () => setTicketTypes(tt => [...tt, { name: '', description: '', price: '', quantity: '' }]);
  const removeTT = (i) => setTicketTypes(tt => tt.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (ticketTypes.some(t => !t.name || !t.quantity)) { toast.error('All ticket types need a name and quantity'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      fd.append('ticket_types', JSON.stringify(ticketTypes.map(t => ({ ...t, price: parseInt(t.price || 0), quantity: parseInt(t.quantity) }))));
      if (banner) fd.append('banner', banner);
      if (fee > 0 && phone) fd.append('phone', phone);
      const res = await eventsAPI.create(fd);
      if (!res.data.requires_payment) {
        toast.success('Event created and published!');
        navigate('/organizer/dashboard');
      } else {
        setCreatedEventId(res.data.id);
        setPaymentPending(true);
        toast.success('✅ M-Pesa prompt sent! Enter your PIN to publish.');
        // Poll for publication
        const interval = setInterval(async () => {
          try {
            const check = await eventsAPI.get(res.data.id);
            if (check.data.status === 'published') {
              clearInterval(interval);
              toast.success('🎉 Payment confirmed! Event is now live!');
              setTimeout(() => navigate('/organizer/dashboard'), 1500);
            }
          } catch {}
        }, 5000);
        setTimeout(() => clearInterval(interval), 300000);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to create event');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      <div style={{ background: '#1A2744', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <Link to="/organizer/dashboard" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14 }}>← Dashboard</Link>
        <span style={{ color: '#fff', fontWeight: 700, fontFamily: "'Playfair Display', serif" }}>Create New Event</span>
      </div>

      <div style={{ maxWidth: 700, margin: '0 auto', padding: '32px 24px' }}>
        <form onSubmit={handleSubmit}>
          {/* Event Details */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20 }}>
            <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#1A2744', margin: '0 0 20px' }}>Event Details</h3>
            {[
              { label: 'Event Name *', key: 'name', placeholder: 'e.g. OWW Annual Gala 2026' },
              { label: 'Venue *', key: 'venue', placeholder: 'e.g. Kisii Golf Club' },
              { label: 'Location / City', key: 'location', placeholder: 'e.g. Kisii, Kenya' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>{f.label}</label>
                <input placeholder={f.placeholder} value={form[f.key]} onChange={e => set(f.key, e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                  required={f.label.includes('*')} />
              </div>
            ))}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Date *</label>
                <input type="date" value={form.date} onChange={e => set('date', e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} required />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Time *</label>
                <input type="time" value={form.time} onChange={e => set('time', e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} required />
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Description</label>
              <textarea placeholder="Tell attendees about this event..." value={form.description} onChange={e => set('description', e.target.value)}
                style={{ width: '100%', padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', minHeight: 100, resize: 'vertical' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Event Banner Image</label>
              <input type="file" accept="image/*" onChange={handleBannerChange}
                style={{ width: '100%', padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
          </div>

          {/* Ticket Types */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#1A2744', margin: 0 }}>Ticket Types</h3>
              <button type="button" onClick={addTT}
                style={{ background: '#f3f4f6', color: '#374151', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                + Add Type
              </button>
            </div>
            {ticketTypes.map((tt, i) => (
              <div key={i} style={{ border: '1px solid #E5E7EB', borderRadius: 10, padding: 16, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ fontWeight: 600, color: '#374151', fontSize: 14 }}>Ticket Type {i + 1}</span>
                  {ticketTypes.length > 1 && (
                    <button type="button" onClick={() => removeTT(i)} style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: 13 }}>Remove</button>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Name *</label>
                    <input placeholder="e.g. VIP, General" value={tt.name} onChange={e => setTT(i, 'name', e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} required />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Price (KSh)</label>
                    <input type="number" placeholder="0 = Free" value={tt.price} onChange={e => setTT(i, 'price', e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} min="0" />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Quantity *</label>
                    <input type="number" placeholder="e.g. 100" value={tt.quantity} onChange={e => setTT(i, 'quantity', e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} min="1" required />
                  </div>
                </div>
                <div style={{ marginTop: 10 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Description (optional)</label>
                  <input placeholder="What's included?" value={tt.description} onChange={e => setTT(i, 'description', e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              </div>
            ))}
          </div>

          {fee > 0 && (
            <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: '#92400E', marginBottom: 8 }}>📱 Event Creation Fee: KSh {fee.toLocaleString()}</div>
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>M-Pesa Phone *</label>
                <input placeholder="e.g. 0712345678" value={phone} onChange={e => setPhone(e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div style={{ fontSize: 12, color: '#92400E' }}>You'll receive an M-Pesa prompt to pay the event creation fee. Your event will be published after payment.</div>
            </div>
          )}

          {paymentPending ? (
            <div style={{ background: '#FEF3C7', borderRadius: 10, padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>⏳</div>
              <p style={{ fontWeight: 700, color: '#92400E' }}>Waiting for payment confirmation...</p>
              <p style={{ color: '#92400E', fontSize: 13 }}>Enter your M-Pesa PIN. Your event will go live automatically.</p>
            </div>
          ) : (
            <button type="submit" disabled={loading || (fee > 0 && !phone)}
              style={{ width: '100%', background: '#E8521A', color: '#fff', padding: 16, borderRadius: 10, border: 'none', fontWeight: 700, fontSize: 16, cursor: (loading || (fee > 0 && !phone)) ? 'not-allowed' : 'pointer', opacity: (loading || (fee > 0 && !phone)) ? 0.7 : 1 }}>
              {loading ? '⏳ Creating...' : fee > 0 ? `🎭 Create Event & Pay KSh ${fee.toLocaleString()}` : '🎭 Create & Publish Event'}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
